import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Select, TreeSelect } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import campaignApi from 'src/component/app/campaign/CampaignApi'
import * as routes from 'src/constants/routes'
import { useUser } from 'src/context/UserContext'
import { AdvertiserBean, CampaignBean, LanguageBean, SspBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { capitalizedView, getIabCategoriesAsTree, isAdmin } from '../../../util/Util'
import GeoTargeting, { GeoItem, toBean } from '../geo/GeoTargeting'
import { MapPoint, MapSelector } from '../map/MapSelector'
import { WithLoadedMap } from '../map/WithLoadedMap'

const FormItem = Form.Item
const Option = Select.Option

const precisionEnum = CampaignBean.GeoPrecisionEnum

export const getGeoPrecision = (geoPrecision: CampaignBean.GeoPrecisionEnum) => {
    switch (geoPrecision) {
        case precisionEnum.GPSLocation: return 'GPS/Location'
        case precisionEnum.IPAddress: return 'IP address'
        case precisionEnum.GPSLocationAndIP: return 'GPS/Location and IP'
    }
}

const CampaignCreate = () => {

    const formInfoItems: FormDataType[] = [
        FormDataType.Languages,
        FormDataType.Advertisers,
        FormDataType.IabCategories
    ]

    const { user } = useUser()

    const [info, setInfo] = useState({
        languages: [],
        advertisers: [],
        ssps: [],
        iabCategories: []
    })

    const navigateTo = useNavigate()
    const [error, setError] = useState()
    const [form] = Form.useForm()
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const [geoItems, setGeoItems] = useState<GeoItem[]>([])

    const [mapPoint, setMapPoint] = useState<MapPoint>({})
    const [mapVisible, setMapVisible] = useState(false)

    const admin = isAdmin(user)

    const loadData = async () => {
        try {
            if (admin) formInfoItems.push(FormDataType.Ssps)

            const resp = await formDataLoader(...formInfoItems)
            setInfo(resp as any)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleSubmit = async () => {
        const values = await form.validateFields()
        try {
            await campaignApi.create({ ...values, startDate, endDate, geoItems: geoItems.map(toBean), ...mapPoint })
            navigateTo('/campaign')
        } catch (e) {
            setError(e.error)
        }
    }

    return <Form
        style={{
            maxWidth: '1000px', margin: '0 auto'
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
    >
        {error ? <Alert message={error} type="error" /> : null}
        <Card title="General" size="small" className="form-card">
            <FormItem
                required
                name="name"
                label="Name"
            >
                <Input />
            </FormItem>
            <FormItem
                name="status"
                label="Status"
                required
            >
                <Select>
                    {Object.values(CampaignBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="advertiserId"
                required
                label="Advertiser"
            >
                <Select
                    showSearch
                    optionFilterProp="title"
                    placeholder="Select Advertiser"
                >
                    {info.advertisers.map((item: AdvertiserBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            {admin && <FormItem
                name="sspIds"
                label="SSP list"
            >
                <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    placeholder="Select SSP"
                >
                    {info.ssps.map((item: SspBean) => <Option key={item.id} value={item.id as number}>{item.name}</Option>)}
                </Select>
            </FormItem>}
            <FormItem
                name="iabCategoryIds"
                label="IAB Categories"
            >
                <TreeSelect
                    allowClear
                    treeCheckable
                    showCheckedStrategy="SHOW_ALL"
                    placeholder="Select IAB categories"
                    treeData={getIabCategoriesAsTree(info.iabCategories)}
                />
            </FormItem>
            <FormItem
                label="Dates"
                required
            >
                <FormItem
                    name="startDate"
                    className="ant-item ant-item-first"
                >
                    <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setStartDate(value)} placeholder="Start date" />
                </FormItem>
                <FormItem
                    name="endDate"
                    className="ant-item ant-item-second"
                >
                    <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setEndDate(value)} placeholder="End date" />
                </FormItem>
            </FormItem>
            <FormItem
                required
                label="Total Budget"
            >
                <FormItem
                    name="totalBudget"
                    className="ant-item ant-item-first"
                >
                    <InputNumber />
                </FormItem>
                <FormItem className="ant-item ant-item-second">$</FormItem>
            </FormItem>
        </Card>
        <Card title="Targeting" size="small" className="form-card">
            <FormItem
                name="languages"
                label="Languages"
            >
                <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    placeholder="Select language"
                >
                    {info.languages.map((item: LanguageBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="devices"
                label="Devices"
            >
                <Select mode="multiple" placeholder="Select device">
                    {Object.keys(CampaignBean.DevicesEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="brands"
                label="Brands"
            >
                <Select mode="multiple" placeholder="Select brand">
                    {Object.keys(CampaignBean.BrandsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="platforms"
                label="Platforms"
            >
                <Select mode="multiple" placeholder="Select platform">
                    {Object.keys(CampaignBean.PlatformsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="browsers"
                label="Browsers"
            >
                <Select mode="multiple" placeholder="Select browser">
                    {Object.keys(CampaignBean.BrowsersEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="contentIabCategoryIds"
                label="Content IAB Categories"
            >
                <TreeSelect
                    allowClear
                    treeCheckable
                    showCheckedStrategy="SHOW_ALL"
                    placeholder="Select content IAB categories"
                    treeData={getIabCategoriesAsTree(info.iabCategories)}
                />
            </FormItem>
        </Card>
        <Card title="Geo" size="small" className="form-card">
            <FormItem label="Coordinates" >
                <FormItem
                    className="ant-item ant-item-first"
                >
                    <InputNumber value={mapPoint.lat} placeholder="Latitude" />
                </FormItem>
                <FormItem
                    className="ant-item ant-item-second"
                >
                    <InputNumber value={mapPoint.lon} placeholder="Longitude" />
                </FormItem>
                <FormItem
                    className="ant-item ant-item-second"
                >
                    <InputNumber value={mapPoint.radius} placeholder="Radius" />
                </FormItem>
                <FormItem
                    className="ant-item ant-item-second"
                >
                    <Button htmlType="button" type="primary" ghost style={{ width: '14.5rem', borderRadius: '16px' }} onClick={() => setMapVisible(true)} >
                        Select on map
                    </Button>
                </FormItem>
            </FormItem>
            <WithLoadedMap>
                <MapSelector
                    point={mapPoint}
                    setPoint={setMapPoint}
                    mapVisible={mapVisible}
                    setMapVisible={setMapVisible}
                />
            </WithLoadedMap>
            <FormItem
                name="geoPrecision"
                label="Geo Precision"
            >
                <Select>
                    {Object.values(precisionEnum).map((type: CampaignBean.GeoPrecisionEnum) => <Option key={type} value={type}>{getGeoPrecision(type)}</Option>)}
                </Select>
            </FormItem>
            <GeoTargeting
                id={undefined}
                loadGeo={undefined}
                geoItems={geoItems}
                setGeoItems={setGeoItems}
            />
        </Card>
        <div className="create-cancel-group">
            <Button type="primary" htmlType="submit">Create</Button>
            <Button htmlType="button" danger onClick={() => navigateTo(routes.CAMPAIGN)}>Cancel</Button>
        </div>
    </Form>
}

export default CampaignCreate


// WEBPACK FOOTER //
// ./src/component/app/campaign/CampaignCreate.tsx